import { Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type Props = {
  /** Icon shown in the colored circle (Lucide component). */
  icon: React.ComponentType<{ className?: string }>;
  /** Big title — what the user is trying to do. */
  title: string;
  /** Explanatory paragraph. */
  description: React.ReactNode;
  /** Bullet list of things they unlock by connecting. */
  bullets?: string[];
};

/**
 * Empty-state block shown on pages that require auth. Stays inside the page
 * layout (no full-screen redirect) so the sidebar remains navigable. The
 * primary CTA points to /login ; secondary nudge to follow forums/users.
 */
export function LoginRequired({ icon: Icon, title, description, bullets }: Props) {
  return (
    <div className="flex justify-center pt-8">
      <Card className="max-w-xl w-full p-8 flex flex-col items-center text-center gap-5">
        <div className="size-14 rounded-full bg-primary/15 text-primary flex items-center justify-center">
          <Icon className="size-7" />
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          <p className="text-sm text-foreground-muted leading-relaxed">{description}</p>
        </div>

        {bullets && bullets.length > 0 && (
          <ul className="text-sm text-foreground-muted flex flex-col gap-1.5 self-stretch px-2 text-left">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span className="mt-1.5 size-1.5 rounded-full bg-primary shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-3 pt-2">
          <Button asChild>
            <Link to="/login">
              <LogIn className="size-4" />
              Se connecter
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/forums">Parcourir les forums</Link>
          </Button>
        </div>

        <p className="text-xs text-foreground-muted">
          Connexion par code à usage unique envoyé par email — pas de mot de passe.
        </p>
      </Card>
    </div>
  );
}
